import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/kit";
import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { FileX, Lightbulb, CheckCircle2, ArrowRight, ChevronDown, ChevronUp, ShieldAlert, ShieldCheck, Eye, Monitor, Users, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { getMetricColor, computeDeliveryScore, LIGHT_COLOR_CLASSES } from "../lib/speechDelivery";
import FeedbackWidget from "../components/FeedbackWidget";
import { useProgressSteps } from "../hooks/useProgressSteps";
import { ProgressSteps } from "../components/ui/ProgressSteps";
import UpgradeModal from "../components/UpgradeModal";

const COACH_STEPS = [
  { label: "Analysing your answer", duration: 4000 },
  { label: "Generating coaching tips", duration: 0 },
];

const MODULE_LABEL = { hr: "HR Round", technical: "Technical Round", behavioral: "Behavioral Round", custom: "Custom" };

const CANDIDATE_RECOMMENDATION = {
  "strong hire":      "Excellent",
  "hire":             "Good",
  "no hire":          "Needs Improvement",
  "strong no hire":   "Needs Improvement",
};
const SCORE_FIELD = { hr: "hr_score", technical: "technical_score", behavioral: "behavioral_score", custom: "custom_score" };
const ROUND_COLORS = { hr: "bg-[#0025D8]", technical: "bg-[#0F172A]", behavioral: "bg-[#7C3AED]" };

const MOD_LABEL = { hr: "HR Round", technical: "Technical", behavioral: "Behavioral", custom: "Custom" };

function DeliverySection({ iv, transcriptModules }) {
  const [expanded, setExpanded] = useState({});

  const allMetrics = [];
  transcriptModules.forEach((m) => {
    (iv.modules[m]?.delivery_metrics || []).forEach((d, i) => {
      if (d) allMetrics.push({ ...d, module: m, qIndex: i, question: iv.modules[m]?.questions?.[i] || "" });
    });
  });

  if (!allMetrics.length) return null;

  const totalFillers = allMetrics.reduce((s, d) => s + d.filler_count, 0);
  const avgWpm = Math.round(allMetrics.reduce((s, d) => s + d.wpm, 0) / allMetrics.length);
  const totalDuration = allMetrics.reduce((s, d) => s + d.answer_duration_sec, 0);
  const worstPause = Math.max(...allMetrics.map((d) => d.longest_pause_sec));
  const totalWeak = allMetrics.reduce((s, d) => s + d.weak_language_count, 0);
  const overallScore = Math.round(allMetrics.reduce((s, d) => s + (computeDeliveryScore(d) || 0), 0) / allMetrics.length);
  const scoreColor = overallScore >= 75 ? "text-[#10B981]" : overallScore >= 50 ? "text-[#F59E0B]" : "text-[#E11D48]";

  const StatCell = ({ label, value, metric, raw }) => {
    const cls = LIGHT_COLOR_CLASSES[getMetricColor(metric, raw)];
    return (
      <div className={`p-4 border-r border-border last:border-r-0 ${cls.bg}`}>
        <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-1">{label}</div>
        <div className={`font-mono text-xl font-bold ${cls.text}`}>{value}</div>
      </div>
    );
  };

  return (
    <div>
      <div className="px-5 py-4 bg-secondary/40 border-b border-border flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em]">DELIVERY ANALYSIS</div>
          <p className="text-xs text-muted-foreground mt-1">
            {allMetrics.length} spoken answer{allMetrics.length !== 1 ? "s" : ""} analysed
          </p>
        </div>
        <div className={`font-display text-3xl font-bold ${scoreColor}`}>
          {overallScore}<span className="text-sm font-normal text-muted-foreground">/100</span>
        </div>
      </div>

      {/* Aggregate stats row */}
      <div className="grid grid-cols-2 md:grid-cols-5 border-b border-border divide-x divide-border">
        <StatCell label="AVG PACE" value={`${avgWpm} WPM`} metric="wpm" raw={avgWpm} />
        <StatCell label="TOTAL FILLERS" value={totalFillers} metric="filler_count" raw={totalFillers} />
        <StatCell label="WORST PAUSE" value={`${worstPause}s`} metric="longest_pause_sec" raw={worstPause} />
        <StatCell label="TOTAL DURATION" value={`${Math.floor(totalDuration / 60)}m ${Math.round(totalDuration % 60)}s`} metric="answer_duration_sec" raw={totalDuration / allMetrics.length} />
        <StatCell label="WEAK PHRASES" value={totalWeak} metric="weak_language_count" raw={totalWeak} />
      </div>

      {/* Per-question expandable rows */}
      {allMetrics.map((d) => {
        const key = `${d.module}-${d.qIndex}`;
        const isOpen = !!expanded[key];
        const score = computeDeliveryScore(d) ?? 0;
        const qScoreColor = score >= 75 ? "text-[#10B981]" : score >= 50 ? "text-[#F59E0B]" : "text-[#E11D48]";
        const mins = Math.floor(d.answer_duration_sec / 60);
        const secs = Math.round(d.answer_duration_sec % 60);

        return (
          <div key={key} className="border-b border-border last:border-b-0">
            <button
              className="w-full flex items-center justify-between p-4 text-left hover:bg-secondary/30 transition-colors"
              onClick={() => setExpanded((s) => ({ ...s, [key]: !s[key] }))}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-mono text-[10px] text-muted-foreground flex-shrink-0">
                  {MOD_LABEL[d.module]} · Q{d.qIndex + 1}
                </span>
                <span className="text-sm font-semibold truncate">{d.question}</span>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                <span className={`font-mono text-sm font-bold ${qScoreColor}`}>{score}/100</span>
                {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </div>
            </button>

            {isOpen && (
              <div className="px-4 pb-4">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
                  {[
                    { label: "WPM",          val: d.wpm,                 display: `${d.wpm}`,             metric: "wpm",                  raw: d.wpm },
                    { label: "Fillers",       val: d.filler_count,        display: `${d.filler_count}`,    metric: "filler_count",         raw: d.filler_count },
                    { label: "Longest pause", val: d.longest_pause_sec,   display: `${d.longest_pause_sec}s`, metric: "longest_pause_sec",  raw: d.longest_pause_sec },
                    { label: "Duration",      val: d.answer_duration_sec, display: `${mins}m ${secs}s`,    metric: "answer_duration_sec",  raw: d.answer_duration_sec },
                    { label: "Weak phrases",  val: d.weak_language_count, display: `${d.weak_language_count}`, metric: "weak_language_count", raw: d.weak_language_count },
                  ].map(({ label, display, metric, raw }) => {
                    const cls = LIGHT_COLOR_CLASSES[getMetricColor(metric, raw)];
                    return (
                      <div key={label} className={`border ${cls.border} ${cls.bg} p-3`}>
                        <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-1">{label}</div>
                        <div className={`font-mono font-bold ${cls.text}`}>{display}</div>
                      </div>
                    );
                  })}
                </div>
                {d.filler_words?.length > 0 && (
                  <p className="font-mono text-[10px] text-muted-foreground mt-1">
                    Fillers: {d.filler_words.map((f) => `"${f.word}" ×${f.count}`).join(" · ")}
                  </p>
                )}
                {d.weak_phrases?.length > 0 && (
                  <p className="font-mono text-[10px] text-muted-foreground mt-0.5">
                    Weak phrases: {d.weak_phrases.join(", ")}
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const CHEAT_TYPE_META = {
  face_missing:    { label: "Face Missing",        color: "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30" },
  attention_drift: { label: "Attention Drift",     color: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30" },
  looking_down:    { label: "Looking Down",        color: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30" },
  looking_away:    { label: "Looking Away",        color: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30" },
  tab_switch:      { label: "Tab Switch",          color: "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30" },
  window_blur:     { label: "Window Blur",         color: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30" },
  multiple_faces:  { label: "Multiple Faces",      color: "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30" },
  audio_coaching:  { label: "Audio Coaching",      color: "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30" },
  paste_detected:  { label: "Content Pasted",      color: "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30" },
};

const MONITORING_CAPABILITIES = [
  { icon: Eye,     label: "Face & Attention" },
  { icon: Monitor, label: "Tab / Window"     },
  { icon: Users,   label: "Multi-person"     },
  { icon: Eye,     label: "Eye Tracking"     },
];

function IntegrityReport({ events }) {
  const [open, setOpen] = useState(false);
  const count = events?.length || 0;
  const isClean = count === 0;

  return (
    <div className="mt-4 rounded-xl border border-border">
      {/* Header row */}
      <div className="flex items-center justify-between px-4 py-3 bg-secondary/30 border-b border-border">
        <div className="flex items-center gap-2">
          {isClean
            ? <ShieldCheck className="w-4 h-4 text-[#10B981]" />
            : <ShieldAlert className="w-4 h-4 text-[#E11D48]" />}
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Integrity Report</span>
        </div>
        <button
          disabled={isClean}
          onClick={() => setOpen((o) => !o)}
          className={`font-mono text-xs font-bold flex items-center gap-1.5 ${isClean ? "text-[#10B981]" : "text-[#E11D48] hover:opacity-70"}`}
        >
          {isClean ? "CLEAN" : `${count} FLAG${count > 1 ? "S" : ""}`}
          {!isClean && <span>{open ? "▲" : "▼"}</span>}
        </button>
      </div>

      {/* Monitoring capabilities */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 px-4 py-2.5">
        {MONITORING_CAPABILITIES.map(({ icon: Icon, label }) => (
          <span key={label} className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground">
            <CheckCircle2 className="w-3 h-3 text-[#10B981]" /> {label}
          </span>
        ))}
      </div>

      {/* Expandable event timeline */}
      {open && count > 0 && (
        <div className="border-t border-border divide-y divide-border">
          <div className="grid grid-cols-12 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground bg-secondary/40">
            <div className="col-span-4 p-2 border-r border-border">Type</div>
            <div className="col-span-2 p-2 border-r border-border">Round</div>
            <div className="col-span-1 p-2 border-r border-border">Q#</div>
            <div className="col-span-3 p-2 border-r border-border">Time</div>
            <div className="col-span-2 p-2">Duration</div>
          </div>
          {events.map((evt, i) => {
            const meta = CHEAT_TYPE_META[evt.type] || { label: evt.type || "Unknown", color: "bg-secondary text-foreground border-border" };
            const roundLabel = { hr: "HR", technical: "Tech", behavioral: "Behav", custom: "Custom" }[evt.module] || evt.module || "—";
            const timeStr = evt.at ? new Date(evt.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";
            const durStr = typeof evt.duration_sec === "number" ? `${evt.duration_sec}s` : "—";
            return (
              <div key={i} className="grid grid-cols-12 text-xs items-center">
                <div className="col-span-4 p-2 border-r border-border">
                  <span className={`inline-block border px-1.5 py-0.5 font-mono text-[10px] font-bold ${meta.color}`}>{meta.label}</span>
                </div>
                <div className="col-span-2 p-2 border-r border-border font-mono text-[10px] text-muted-foreground">{roundLabel}</div>
                <div className="col-span-1 p-2 border-r border-border font-mono text-[10px] text-muted-foreground">{evt.q != null ? `Q${evt.q + 1}` : "—"}</div>
                <div className="col-span-3 p-2 border-r border-border font-mono text-[10px] text-muted-foreground">{timeStr}</div>
                <div className="col-span-2 p-2 font-mono text-[10px] text-muted-foreground">{durStr}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CoachPanel({ open, loading, data, onToggle }) {
  const coachProgress = useProgressSteps(COACH_STEPS);
  useEffect(() => {
    if (loading) coachProgress.start();
    else coachProgress.finish();
  }, [loading]); // eslint-disable-line

  return (
    <div className="mt-3">
      <button
        onClick={onToggle}
        className={`group flex items-center gap-2 px-3 py-1.5 text-xs font-bold border transition-all duration-150 ${
          open
            ? "bg-[#7C3AED] text-white border-[#7C3AED]"
            : "bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/50 hover:bg-[#7C3AED] hover:text-white hover:border-[#7C3AED]"
        }`}
      >
        <Lightbulb className={`w-3.5 h-3.5 transition-transform duration-150 ${open ? "" : "group-hover:scale-110"}`} />
        Coach me on this
        {open ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
      </button>

      {open && (
        <div className="border border-[#7C3AED]/30 border-t-0 bg-white p-4">
          {loading ? (
            <ProgressSteps steps={COACH_STEPS} currentStep={coachProgress.currentStep} pct={coachProgress.pct} />
          ) : data ? (
            <div className="space-y-4">
              {data.strengths?.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] font-bold text-[#10B981] tracking-[0.2em] mb-2 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3" /> WHAT YOU DID WELL
                  </div>
                  <ul className="space-y-1.5">
                    {data.strengths.map((s, si) => (
                      <li key={si} className="flex items-start gap-2 text-sm leading-snug">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#10B981] flex-shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.tips?.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] font-bold text-[#7C3AED] tracking-[0.2em] mb-2 flex items-center gap-1.5">
                    <ArrowRight className="w-3 h-3" /> HOW TO IMPROVE
                  </div>
                  <ul className="space-y-1.5">
                    {data.tips.map((t, ti) => (
                      <li key={ti} className="flex items-start gap-2 text-sm leading-snug">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#7C3AED] flex-shrink-0" />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function ScoreRing({ value, label }) {
  const v = Math.round(value || 0);
  const data = [{ name: label, value: v, fill: v < 40 ? "#E11D48" : v < 70 ? "#F59E0B" : "#0025D8" }];
  return (
    <div className="rounded-xl border border-border p-4 bg-white">
      <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-2">{label}</div>
      <div className="h-40 relative">
        <ResponsiveContainer>
          <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar background dataKey="value" cornerRadius={0} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="font-display text-5xl font-black">{v}</div>
            <div className="font-mono text-[10px] text-muted-foreground">/ 100</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Results() {
  const { id } = useParams();
  const { user } = useAuth();
  const [iv, setIv] = useState(null);
  const [coachOpen, setCoachOpen] = useState({});
  const [coachData, setCoachData] = useState({});
  const [coachLoading, setCoachLoading] = useState({});
  const [reportTab, setReportTab] = useState("delivery");
  const [feedbackDismissed, setFeedbackDismissed] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/interviews/${id}`);
      setIv(data);
      // Pre-populate saved coaching tips from DB
      const saved = {};
      Object.entries(data.modules || {}).forEach(([mod, modData]) => {
        Object.entries(modData.coaching_tips || {}).forEach(([qi, tips]) => {
          saved[`${mod}-${qi}`] = tips;
        });
      });
      if (Object.keys(saved).length) setCoachData(saved);
    })();
  }, [id]);

  if (!iv) return <div className="p-12 font-mono text-sm text-muted-foreground">Loading report…</div>;

  const isCandidate = user?.role === "candidate";
  const isCustomRole = iv.source === "custom_role";
  const isRoleBased = iv.source === "role_based";

  const barData = [];
  ["hr", "technical", "behavioral", "custom"].forEach((m) => {
    if (iv.modules?.[m]?.questions?.length) {
      barData.push({ module: m, name: MODULE_LABEL[m], score: Math.round(iv[SCORE_FIELD[m]] || 0) });
    }
  });
  const transcriptModules = ["hr", "technical", "behavioral", "custom"].filter(
    (m) => iv.modules?.[m]?.questions?.length > 0
  );

  const fetchCoach = async (module, q_index) => {
    const key = `${module}-${q_index}`;
    if (coachData[key]) { setCoachOpen((s) => ({ ...s, [key]: !s[key] })); return; }
    setCoachLoading((s) => ({ ...s, [key]: true }));
    setCoachOpen((s) => ({ ...s, [key]: true }));
    try {
      const { data } = await api.post(`/interviews/${id}/coach`, { module, q_index });
      setCoachData((s) => ({ ...s, [key]: data.coaching }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate coaching tips");
      setCoachOpen((s) => ({ ...s, [key]: false }));
    } finally {
      setCoachLoading((s) => ({ ...s, [key]: false }));
    }
  };

  const wideLayout = isRoleBased || isCustomRole;

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      {iv.source === "onboarding" && (
        <div className="border border-[#7C3AED]/30 bg-[#7C3AED]/5 p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#7C3AED] flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-mono text-[10px] text-[#7C3AED] tracking-[0.2em] mb-1">DEMO COMPLETE — GREAT START!</div>
              <h2 className="font-display font-bold text-xl mb-1">You just finished your 3-question demo interview.</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Now see how you do in a full structured session — pick a role, select your rounds, and get detailed per-module feedback.
              </p>
              <Link to="/interviews/new">
                <Button variant="purple">Start your first real interview</Button>
              </Link>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0025D8]">Result Report</div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <h1 className="font-display font-black text-4xl tracking-tight">{iv.job_title}</h1>
            {isRoleBased && iv.round && (
              <span className={`px-3 py-1 rounded-md text-xs font-mono font-bold uppercase tracking-wider text-white ${ROUND_COLORS[iv.round] || "bg-secondary"}`}>
                {MODULE_LABEL[iv.round] || iv.round}
              </span>
            )}
            {isCustomRole && (
              <span className="px-3 py-1 rounded-md text-xs font-mono font-bold uppercase tracking-wider text-white bg-[#7C3AED]">CUSTOM</span>
            )}
            <span className="flex items-center gap-1 px-3 py-1 rounded-md text-xs font-mono font-bold uppercase tracking-wider text-[#10B981] border border-[#10B981]/40 bg-[#10B981]/5">
              <ShieldCheck className="w-3 h-3" /> AI Monitored
            </span>
          </div>
          <div className="text-muted-foreground mt-2">Last graded {iv.completed_at ? new Date(iv.completed_at).toLocaleString() : "—"}</div>

          {/* Admin: skill coverage summary */}
          {!isCandidate && isRoleBased && (iv.candidate_tools?.length > 0 || iv.coverage_gaps?.length > 0) && (
            <div className="mt-4 flex flex-wrap gap-4">
              {iv.candidate_tools?.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-1.5">CANDIDATE TOOLS DETECTED</div>
                  <div className="flex flex-wrap gap-1">
                    {iv.candidate_tools.map((t) => (
                      <span key={t} className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#0025D8]/10 text-[#0025D8] border border-[#0025D8]/20">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {iv.coverage_gaps?.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-1.5">BANK COVERAGE GAPS</div>
                  <div className="flex flex-wrap gap-1">
                    {iv.coverage_gaps.map((g) => (
                      <span key={g} className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/30">{g}</span>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">No questions in bank for these categories — regenerate the bank to improve coverage.</p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Link to="/dashboard"><Button variant="secondary" data-testid="back-dashboard">Back to dashboard</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-10">
        {!wideLayout && (
          <div className="md:col-span-4"><ScoreRing label="JD MATCH" value={iv.jd_match_percent} /></div>
        )}
        <div className={wideLayout ? "md:col-span-6" : "md:col-span-4"}>
          <ScoreRing label="OVERALL" value={iv.overall_score} />
        </div>
        <div className={`${wideLayout ? "md:col-span-6" : "md:col-span-4"} rounded-xl border border-border p-6 bg-white`}>
          <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em]">
            {isCandidate ? "AI ASSESSMENT" : "RECOMMENDATION"}
          </div>
          <div className="font-display text-3xl font-bold mt-2">
            {isCandidate
              ? (CANDIDATE_RECOMMENDATION[(iv.recommendation || "").toLowerCase()] ?? iv.recommendation ?? "—")
              : (iv.recommendation || "—")}
          </div>
          <p className="text-sm text-muted-foreground mt-3">{iv.summary}</p>
        </div>
      </div>

      {/* Module Breakdown — always visible, full width */}
      {barData.length > 0 && (
        <div className="rounded-xl border border-border bg-white mb-6">
          <div className="px-6 py-4 border-b border-border bg-secondary/40">
            <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em]">MODULE BREAKDOWN</div>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {barData.map(({ module: m, name, score }) => {
              const color = score >= 70 ? "#10B981" : score >= 40 ? "#F59E0B" : "#E11D48";
              const summary = isRoleBased
                ? iv.summary || ""
                : iv[`${m}_summary`] || "";
              return (
                <div key={m} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{name}</span>
                    <span className="font-mono text-sm font-bold" style={{ color }}>{score}<span className="font-normal text-muted-foreground text-xs">/100</span></span>
                  </div>
                  <div className="w-full h-1.5 bg-secondary">
                    <div className="h-1.5 transition-all duration-500" style={{ width: `${score}%`, backgroundColor: color }} />
                  </div>
                  {summary && <p className="text-xs text-muted-foreground leading-relaxed">{summary}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Strengths + Weaknesses */}
      <div className="rounded-xl border border-border bg-white mb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
          <div className="p-6">
            <div className="font-mono text-[10px] text-[#10B981] tracking-[0.2em] mb-3">STRENGTHS</div>
            <ul className="space-y-2 text-sm">
              {(iv.strengths || []).map((s, i) => (<li key={i} className="flex gap-2"><span className="text-[#10B981] flex-shrink-0">+</span>{s}</li>))}
              {(!iv.strengths || iv.strengths.length === 0) && <li className="text-muted-foreground">—</li>}
            </ul>
          </div>
          <div className="p-6">
            <div className="font-mono text-[10px] text-[#E11D48] tracking-[0.2em] mb-3">WEAKNESSES</div>
            <ul className="space-y-2 text-sm">
              {(iv.weaknesses || []).map((s, i) => (<li key={i} className="flex gap-2"><span className="text-[#E11D48] flex-shrink-0">−</span>{s}</li>))}
              {(!iv.weaknesses || iv.weaknesses.length === 0) && <li className="text-muted-foreground">—</li>}
            </ul>
          </div>
        </div>
      </div>

      {/* Session Report — tabbed: Delivery | Integrity */}
      <div className="rounded-xl border border-border mb-10">
        <div className="flex border-b border-border bg-secondary/40">
          {["delivery", "integrity"].map((tab) => (
            <button
              key={tab}
              onClick={() => setReportTab(tab)}
              className={`px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] border-b-2 transition-colors -mb-px ${
                reportTab === tab
                  ? "border-[#0025D8] text-[#0025D8] bg-white"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab === "delivery" ? "Delivery Analysis" : "Integrity Report"}
            </button>
          ))}
        </div>
        <div className="p-0">
          {reportTab === "delivery"
            ? <DeliverySection iv={iv} transcriptModules={transcriptModules} />
            : <IntegrityReport events={iv.cheat_events || []} />}
        </div>
      </div>

      <div className="rounded-xl border border-border">
        <div className="p-6 bg-secondary/40 font-mono text-[10px] tracking-[0.2em] border-b border-border">MODULES · TRANSCRIPT & HISTORY</div>

        {/* Legacy custom module banner (JD-based interviews) */}
        {iv.modules?.custom?.questions?.length > 0 && (iv.custom_summary || (iv.custom_missing_count ?? 0) > 0) && (
          <div className="px-6 py-4 border-b border-border bg-[#0025D8]/5">
            <div className="font-mono text-[10px] tracking-[0.2em] text-[#0025D8] mb-2">CUSTOM PHASE · DOCUMENT-GROUNDED REVIEW</div>
            {iv.custom_summary && <p className="text-sm">{iv.custom_summary}</p>}
            {(iv.custom_missing_count ?? 0) > 0 && (
              <div className="mt-2 text-sm text-[#E11D48] flex items-center gap-2">
                <FileX className="w-4 h-4" /> {iv.custom_missing_count} question(s) could not be answered from the uploaded documents.
              </div>
            )}
          </div>
        )}

        {transcriptModules.map((m) => {
          const mod = iv.modules[m];
          const attempts = mod.attempts || [];
          const useDocStyle = m === "custom" || isCustomRole;

          return (
            <div key={m} className="p-6 border-b border-border">
              <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
                <div>
                  <div className="font-display font-bold text-xl">{MODULE_LABEL[m]}</div>
                  <div className="font-mono text-xs text-muted-foreground mt-1">
                    Score: <span className="text-foreground font-bold">{Math.round(iv[SCORE_FIELD[m]] || 0)}</span> / 100
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {useDocStyle
                  ? mod.questions.map((q, i) => {
                      const items = attempts?.[attempts.length - 1]?.items || iv.custom_items || [];
                      const it = items.find((x) => x.q_index === i) || {};
                      const found = it.found_in_docs;
                      const score = it.intent_score;
                      const qRetakes = (mod.question_retakes || []).filter((r) => r.q_index === i);
                      const retakeKey = `${m}-${i}`;

                      return (
                        <div key={i} className="rounded-xl border border-border bg-white p-4">
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                            <div className="md:col-span-5">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="font-mono text-[10px] text-muted-foreground">Q{i + 1}</span>
                                {qRetakes.length > 0 && (
                                  <div className="flex items-center gap-1 flex-wrap">
                                    {qRetakes.map((rt, ri) => {
                                      const s = rt.score ?? rt.intent_score;
                                      const color = s >= 70 ? "#10B981" : s >= 40 ? "#F59E0B" : "#E11D48";
                                      return s != null ? (
                                        <span key={ri} className="font-mono text-[9px] px-1.5 py-0.5 border" style={{ borderColor: color, color }}>
                                          #{ri + 1} {s}/100
                                        </span>
                                      ) : null;
                                    })}
                                  </div>
                                )}
                                {found === false ? (
                                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#E11D48] flex items-center gap-1">
                                    <FileX className="w-3 h-3" />
                                    {isCustomRole ? "OUT-OF-SCOPE" : "NOT IN DOCS"}
                                  </span>
                                ) : found === true ? (
                                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#10B981]">FOUND IN DOCS</span>
                                ) : null}
                                {typeof score === "number" && (
                                  <span className="font-mono text-xs ml-auto md:hidden">{score}/100</span>
                                )}
                              </div>
                              <div className="font-semibold">{q}</div>
                              {it.doc_excerpt && (
                                <div className="mt-3 border-l-2 border-[#0025D8] pl-3 text-xs text-muted-foreground italic">
                                  "{it.doc_excerpt}"
                                </div>
                              )}
                            </div>
                            <div className="md:col-span-7">
                              {mod.follow_up_conversations?.[i]?.length > 0 ? (
                                <div className="text-sm bg-secondary/40 p-3 rounded-xl border border-border">
                                  <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-2">CONVERSATION</div>
                                  <div className="space-y-2">
                                    {mod.follow_up_conversations[i].map((turn, ti) => (
                                      <div key={ti} className={`p-2 ${turn.role === "interviewer" ? "bg-white border-l-2 border-[#0025D8]" : "bg-[#0025D8]/5 border-l-2 border-[#10B981]"}`}>
                                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-0.5">
                                          {turn.role === "interviewer" ? "INTERVIEWER" : "CANDIDATE"}
                                        </div>
                                        <div>{turn.text}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm bg-secondary/40 p-3 rounded-xl border border-border">
                                  <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-1">CANDIDATE ANSWER</div>
                                  {mod.answers[i] || <em>No answer recorded.</em>}
                                </div>
                              )}
                              {(it.feedback || found === false) && (
                                <div className={`mt-2 p-3 border text-sm ${found === false ? "border-[#E11D48]/40 bg-[#E11D48]/5 text-[#E11D48]" : "border-border bg-white"}`}>
                                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] mb-1">
                                    {found === false ? "AI NOTE" : "FEEDBACK"}
                                    {typeof score === "number" && <span className="ml-2 text-foreground">· {score}/100</span>}
                                  </div>
                                  {it.feedback || "Answer for this question was not found in the uploaded reference documents."}
                                </div>
                              )}

                              {/* Coach me on this */}
                              {isCandidate && mod.answers[i] && (
                                <CoachPanel
                                  open={!!coachOpen[`${m}-${i}`]}
                                  loading={!!coachLoading[`${m}-${i}`]}
                                  data={coachData[`${m}-${i}`]}
                                  onToggle={() => fetchCoach(m, i)}
                                />
                              )}

                              {/* Retake history */}
                              {qRetakes.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em]">RETAKE HISTORY</div>
                                  {qRetakes.map((rt, ri) => (
                                    <div key={ri} className="rounded-xl border border-border p-3 bg-secondary/20">
                                      <div className="font-mono text-[10px] text-muted-foreground mb-1 flex flex-wrap gap-2">
                                        <span>RETAKE {ri + 1}</span>
                                        {rt.intent_score != null && (
                                          <span className="text-foreground font-bold">· {rt.intent_score}/100</span>
                                        )}
                                        {rt.found_in_docs === false && (
                                          <span className="text-[#E11D48]">· OUT-OF-SCOPE</span>
                                        )}
                                        {rt.completed_at && (
                                          <span>{new Date(rt.completed_at).toLocaleString()}</span>
                                        )}
                                      </div>
                                      <div className="text-sm mb-2">{rt.answer}</div>
                                      {rt.feedback && (
                                        <div className="text-xs text-muted-foreground border-t border-border pt-2">{rt.feedback}</div>
                                      )}
                                      {rt.doc_excerpt && (
                                        <div className="mt-2 border-l-2 border-[#0025D8] pl-3 text-xs text-muted-foreground italic">
                                          "{rt.doc_excerpt}"
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Per-question retake */}
                              {isCandidate && mod.answers[i] && iv.source !== "onboarding" && (
                                <div className="mt-3">
                                  <button
                                    onClick={() => user?.plan === "basic" ? setUpgradeOpen(true) : navigate(`/auto-interview/${id}?retake_module=${m}&retake_q=${i}`)}
                                    className="group flex items-center gap-2 px-3 py-1.5 text-xs font-bold border transition-all duration-150 bg-[#0025D8]/10 text-[#0025D8] border-[#0025D8]/50 hover:bg-[#0025D8] hover:text-white hover:border-[#0025D8]"
                                  >
                                    <RotateCcw className="w-3.5 h-3.5 transition-transform duration-150 group-hover:rotate-[-45deg]" />
                                    Retake this question
                                    {user?.plan === "basic" && <span className="ml-1 opacity-60">· Premium</span>}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  : mod.questions.map((q, i) => {
                      const qRetakes = (mod.question_retakes || []).filter((r) => r.q_index === i);
                      const retakeKey = `${m}-${i}`;
                      return (
                        <div key={i} className="rounded-xl border border-border bg-white p-4">
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                            <div className="md:col-span-5">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="font-mono text-[10px] text-muted-foreground">Q{i + 1}</span>
                                {qRetakes.length > 0 && (
                                  <div className="flex items-center gap-1 flex-wrap">
                                    {qRetakes.map((rt, ri) => {
                                      const s = rt.score ?? rt.intent_score;
                                      const color = s >= 70 ? "#10B981" : s >= 40 ? "#F59E0B" : "#E11D48";
                                      return s != null ? (
                                        <span key={ri} className="font-mono text-[9px] px-1.5 py-0.5 border" style={{ borderColor: color, color }}>
                                          #{ri + 1} {s}/100
                                        </span>
                                      ) : null;
                                    })}
                                  </div>
                                )}
                              </div>
                              <div className="font-semibold">{q}</div>
                              {/* Retake history */}
                              {qRetakes.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  {qRetakes.map((rt, ri) => (
                                    <div key={ri} className="rounded-xl border border-border p-3 bg-secondary/20">
                                      <div className="font-mono text-[10px] text-muted-foreground mb-1 flex flex-wrap gap-2">
                                        <span>RETAKE {ri + 1}</span>
                                        {rt.score != null && (
                                          <span className="text-foreground font-bold">· {rt.score}/100</span>
                                        )}
                                        {rt.completed_at && <span>{new Date(rt.completed_at).toLocaleString()}</span>}
                                      </div>
                                      <div className="text-sm mb-1">{rt.answer}</div>
                                      {rt.feedback && (
                                        <div className="text-xs text-muted-foreground border-t border-border pt-2">{rt.feedback}</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="md:col-span-7">
                              {mod.follow_up_conversations?.[i]?.length > 0 ? (
                                <div className="text-sm bg-secondary/40 p-3 rounded-xl border border-border">
                                  <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-2">CONVERSATION</div>
                                  <div className="space-y-2">
                                    {mod.follow_up_conversations[i].map((turn, ti) => (
                                      <div key={ti} className={`p-2 ${turn.role === "interviewer" ? "bg-white border-l-2 border-[#0025D8]" : "bg-[#0025D8]/5 border-l-2 border-[#10B981]"}`}>
                                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-0.5">
                                          {turn.role === "interviewer" ? "INTERVIEWER" : "CANDIDATE"}
                                        </div>
                                        <div>{turn.text}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm bg-secondary/40 p-3 rounded-xl border border-border">
                                  <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-1">CANDIDATE ANSWER</div>
                                  {mod.answers[i] || <em>No answer recorded.</em>}
                                </div>
                              )}
                              {isCandidate && mod.answers[i] && (
                                <CoachPanel
                                  open={!!coachOpen[`${m}-${i}`]}
                                  loading={!!coachLoading[`${m}-${i}`]}
                                  data={coachData[`${m}-${i}`]}
                                  onToggle={() => fetchCoach(m, i)}
                                />
                              )}
                              {/* Per-question retake */}
                              {isCandidate && mod.answers[i] && iv.source !== "onboarding" && (
                                <div className="mt-3">
                                  <button
                                    onClick={() => user?.plan === "basic" ? setUpgradeOpen(true) : navigate(`/auto-interview/${id}?retake_module=${m}&retake_q=${i}`)}
                                    className="group flex items-center gap-2 px-3 py-1.5 text-xs font-bold border transition-all duration-150 bg-[#0025D8]/10 text-[#0025D8] border-[#0025D8]/50 hover:bg-[#0025D8] hover:text-white hover:border-[#0025D8]"
                                  >
                                    <RotateCcw className="w-3.5 h-3.5 transition-transform duration-150 group-hover:rotate-[-45deg]" />
                                    Retake this question
                                    {user?.plan === "basic" && <span className="ml-1 opacity-60">· Premium</span>}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
              </div>
            </div>
          );
        })}
      </div>

      {isCandidate && !feedbackDismissed && iv.status === "completed" && (
        <div className="max-w-3xl mx-auto px-4 pb-10 mt-6">
          <FeedbackWidget
            interviewId={iv.interview_id}
            interviewRole={iv.job_title}
            contextType="post_interview"
            onDismiss={() => setFeedbackDismissed(true)}
          />
        </div>
      )}
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason="retake" />
    </main>
  );
}
